import { api } from './client'
import type { LedgerSourceClaim } from './ledger-source-claim'

// ============================================================================
// Sổ đóng gói 12,5g (#M-16) — ghi chép của xưởng, tách khỏi phiếu kho.
//
// Người đóng gói ghi từng mẻ; kế toán bấm một nút để mở màn tạo phiếu nhập lô
// sản phẩm với dữ liệu điền sẵn. Sổ KHÔNG đụng tồn kho: tồn chỉ đổi khi phiếu
// được duyệt.
// ============================================================================

const BASE = '/product-lots/packaging-log-entries'

/** draft = chưa lập phiếu · synced = khớp kho · diverged = sửa sau khi có phiếu. */
export type PackagingLogSyncState = 'draft' | 'synced' | 'diverged'

export interface PackagingLogEntrySource {
    id: string
    entry_id: string
    source_scenting_lot_id: string
    quantity: number
    position: number
    /** Mã lô — CHỈ để điền sang phiếu; giao diện sổ hiện "Loại — ngày lấy hương". */
    source_lot_code?: string
    source_grade_name?: string
    source_created_at?: string
    /** Ngày lấy hương của lô nguồn; trống với lô cũ (M-63). */
    source_scenting_date?: string | null
    source_available: number
    /** kg khả dụng của lô lúc chọn, lưu theo từng dòng lô (M-72). Mẻ ghi
     *  trước M-72 trả kg xuất; server cũ chưa trả trường này. */
    source_available_snapshot?: number
}

export interface PackagingLogEntryOutput {
    id: string
    entry_id: string
    product_id: string
    /** Số lượng thành phẩm, đơn vị là GÓI. */
    quantity: number
    discount_percent: number
    product_lot_id?: string | null
    position: number
    product_name?: string
    product_sku?: string
    product_lot_code?: string
}

export interface PackagingLogEntry {
    /** Người làm mẻ (#392). Mẻ ghi trước #392 để trống. */
    employee_id?: string | null
    employee_name?: string
    id: string
    packaging_date: string
    notes?: string | null
    note_id?: string | null
    note_code?: string
    note_status?: '' | 'pending' | 'approved' | 'rejected' | 'cancelled'
    sync_state: PackagingLogSyncState
    /** Bốn trường dưới để giao diện nói rõ vì sao dòng bị khoá TRƯỚC khi bấm. */
    can_edit: boolean
    can_delete: boolean
    lock_reason?: string
    adjust_block_reason?: string
    /** Lô trên phiếu không khớp các dòng đích của sổ — ngừng so lệch. */
    note_lot_mismatch?: boolean
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
    sources: PackagingLogEntrySource[]
    outputs: PackagingLogEntryOutput[]
}

export interface PackagingLogEntryInput {
    /** Nhân sự thực sự làm mẻ này (#392) — bắt buộc. */
    employee_id: string
    packaging_date: string
    notes?: string
    sources: Array<{
        source_lot_id: string
        quantity: number
        /** kg khả dụng người ghi đã thấy lúc chọn lô (M-72). */
        available_snapshot?: number
    }>
    outputs: Array<{
        product_id: string
        quantity: number
        discount_percent?: number
    }>
}

export interface ListPackagingLogParams {
    date_from?: string
    date_to?: string
    sync_state?: PackagingLogSyncState
    created_by?: string
    page?: number
    per_page?: number
}

/** Một lô lệch. */
export interface PackagingLogLotDelta {
    LotID: string
    Quantity: number
}

/** Bốn nhóm chênh lệch, mỗi nhóm ứng với một loại phiếu điều chỉnh sẽ sinh ra. */
export interface PackagingLogDivergence {
    ProductIncrease: PackagingLogLotDelta[] | null
    ProductDecrease: PackagingLogLotDelta[] | null
    TeaTakeMore: PackagingLogLotDelta[] | null
    TeaGiveBack: PackagingLogLotDelta[] | null
}

export type PackagingAdjustedNoteKind =
    'product_in' | 'product_out' | 'scenting_in' | 'scenting_out'

export interface PackagingAdjustedNote {
    id: string
    code: string
    kind: PackagingAdjustedNoteKind
    quantity: number
}

export const packagingLogApi = {
    list: (params?: ListPackagingLogParams) =>
        api.get<PackagingLogEntry[]>(BASE, { params }),
    get: (id: string) => api.get<PackagingLogEntry>(`${BASE}/${id}`),
    divergence: (id: string) =>
        api.get<PackagingLogDivergence>(`${BASE}/${id}/divergence`),
    /** M-140: phần lô nguồn đang bị các mẻ chưa vào kho KHÁC giữ — ô chọn lô
     *  trừ sẵn. `excludeEntryId` = mẻ đang sửa. */
    sourceClaims: (excludeEntryId?: string) =>
        api.get<LedgerSourceClaim[]>(`${BASE}/source-claims`, {
            params: excludeEntryId
                ? { exclude_entry_id: excludeEntryId }
                : undefined,
        }),
    create: (req: PackagingLogEntryInput) =>
        api.post<PackagingLogEntry>(BASE, req),
    update: (id: string, req: PackagingLogEntryInput) =>
        api.put<PackagingLogEntry>(`${BASE}/${id}`, req),
    remove: (id: string) => api.delete<void>(`${BASE}/${id}`),
    adjust: (id: string) =>
        api.post<PackagingAdjustedNote[]>(`${BASE}/${id}/adjust`),
    /** M-87: đẩy tem lô của một dòng sản phẩm vào hàng đợi in cho máy in ở kho. */
    printOutputLabel: (outputId: string) =>
        api.post<{ job_id: string }>(`${BASE}/outputs/${outputId}/print-label`),
}
